import { useState, useEffect, useCallback, useRef } from "react";
import { Gauge, X, RefreshCw, Cable, Users, Split, TriangleAlert, Server, Router, Network, Radio, Zap, ArrowUp, ArrowDown, Globe2 } from "lucide-react";
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, Tooltip, ResponsiveContainer } from "recharts";
import { toast } from "sonner";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";

const isOnline = (d) => !!d?.last_inform && Date.now() - new Date(d.last_inform).getTime() < 15 * 60 * 1000;

const Tile = ({ icon: Icon, tint, value, sub, label, testid, trend }) => (
  <div data-testid={testid} className="kv-glass rounded-xl p-4 kv-anim-in hover:border-cyan-500/40 transition-colors">
    <div className="flex items-center justify-between">
      <Icon size={18} className={tint} />
      <span className={`w-1.5 h-1.5 rounded-full ${tint.replace("text-", "bg-")} animate-pulse`} />
    </div>
    <div className="mt-2 text-2xl font-bold text-white" style={{ fontFamily: "'Manrope', sans-serif" }}>
      {value}{sub && <span className="text-sm text-slate-400 font-normal"> {sub}</span>}
      {typeof trend === "number" && trend !== 0 && (
        <span data-testid="rx-trend" title="Zmiana względem poprzedniego odczytu" className={`ml-1.5 inline-flex items-center text-xs align-middle ${trend > 0 ? "text-rose-400" : "text-emerald-400"}`}>
          {trend > 0 ? <ArrowUp size={14} /> : <ArrowDown size={14} />}{Math.abs(trend)}
        </span>
      )}
    </div>
    <div className="text-[11px] uppercase tracking-wider text-cyan-400/70 mt-0.5">{label}</div>
  </div>
);

export default function DashboardPanel({ onClose }) {
  const { t } = useI18n();
  const [s, setS] = useState(null);
  const [routers, setRouters] = useState([]);
  const [switches, setSwitches] = useState([]);
  const [rxAlerts, setRxAlerts] = useState([]);
  const [rxTrend, setRxTrend] = useState(0);
  const prevRxRef = useRef(null);
  const [autoRx, setAutoRx] = useState({ enabled: false, interval: 60, threshold: 3 });
  const [geoPending, setGeoPending] = useState(0);
  const [hasTeryt, setHasTeryt] = useState(true);
  const [sidusis, setSidusis] = useState(null);

  const load = useCallback(() => {
    api.get("/stats").then((r) => setS(r.data)).catch(() => {});
    api.get("/tr069/devices").then((r) => setRouters(r.data || [])).catch(() => {});
    api.get("/switchmgmt/devices").then((r) => setSwitches(r.data || [])).catch(() => {});
    api.get("/settings").then((r) => setAutoRx({ enabled: !!r.data.rx_autopoll_enabled, interval: r.data.rx_autopoll_interval_min || 60, threshold: r.data.rx_alert_threshold_db ?? 3, lastRun: r.data.rx_autopoll_last_run || null, lastCount: r.data.rx_autopoll_last_count ?? null })).catch(() => {});
    api.get("/optical/rx-alerts").then((r) => {      const list = r.data || [];
      setRxAlerts(list);
      setRxTrend(prevRxRef.current === null ? 0 : list.length - prevRxRef.current);
      prevRxRef.current = list.length;
    }).catch(() => {});
    api.get("/clients/teryt-unmatched").then((r) => { setGeoPending(r.data?.unmatched || 0); setHasTeryt(r.data?.has_teryt_base ?? true); }).catch(() => {});
    api.get("/sidusis/status").then((r) => setSidusis(r.data)).catch(() => {});
  }, []);
  const regeocode = async () => {
    try {
      const { data } = await api.post("/clients/rematch-teryt");
      toast.success(`Ponowne dopasowanie TERYT: ${data.queued} klientów w kolejce`);
      setTimeout(load, 6000);
    } catch (e) { toast.error(e.response?.data?.detail || "Błąd dopasowania"); }
  };
  const saveAutoRx = (patch) => {    const next = { ...autoRx, ...patch };
    setAutoRx(next);
    api.put("/settings", { rx_autopoll_enabled: next.enabled, rx_autopoll_interval_min: Math.max(15, Number(next.interval) || 60), rx_alert_threshold_db: Math.max(1, Number(next.threshold) || 3) })
      .then(() => toast.success("Zapisano ustawienia auto-pomiaru RX")).catch((e) => toast.error(e.response?.data?.detail || "Błąd zapisu (tylko administrator)"));
  };
  useEffect(() => { load(); const id = setInterval(load, 20000); return () => clearInterval(id); }, [load]);

  const c = s?.counts || {};
  const routersOnline = routers.filter(isOnline).length;
  const onuOnline = s?.onu_online ?? 0;
  const onuOffline = Math.max(0, (s?.onu_total ?? 0) - onuOnline);
  const onuData = [{ name: t("online"), value: onuOnline, color: "#34d399" }, { name: t("offline"), value: onuOffline, color: "#f87171" }];
  const infra = [
    { name: t("Słupy"), v: c.poles ?? 0 }, { name: t("Mufy"), v: c.closures ?? 0 },
    { name: t("Splittery"), v: c.splitters ?? 0 }, { name: t("Kable"), v: c.cables ?? 0 },
    { name: "OLT", v: c.olt ?? 0 },
  ];

  return (
    <div className="absolute inset-0 flex flex-col h-full" data-testid="dashboard-panel">
      <div className="flex items-center justify-between px-4 py-3 kv-glass border-0 border-b border-cyan-500/15">
        <div className="flex items-center gap-2 text-slate-100 font-bold" style={{ fontFamily: "'Manrope', sans-serif" }}>
          <Gauge size={18} className="text-cyan-400" /> {t("Pulpit NOC — FiberPass")}
        </div>
        <div className="flex items-center gap-1">
          <button onClick={load} data-testid="dashboard-refresh" className="p-1.5 rounded-lg hover:bg-cyan-500/15 text-slate-400 hover:text-cyan-300"><RefreshCw size={15} /></button>
          <button onClick={onClose} data-testid="dashboard-close" className="p-1.5 rounded-lg hover:bg-slate-700 text-slate-400"><X size={18} /></button>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-4 space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Tile testid="tile-onu" icon={Radio} tint="text-emerald-400" value={onuOnline} sub={`/ ${s?.onu_total ?? 0}`} label={t("ONU online")} />
          <Tile testid="tile-olt" icon={Server} tint="text-sky-400" value={c.olt ?? 0} label="OLT" />
          <Tile testid="tile-faults" icon={TriangleAlert} tint={(s?.active_faults ?? 0) > 0 ? "text-rose-400" : "text-amber-400"} value={s?.active_faults ?? 0} label={t("Aktywne awarie")} />
          <Tile testid="tile-km" icon={Cable} tint="text-cyan-400" value={s?.cable_total_km ?? 0} sub="km" label={t("Długość kabli")} />
          <Tile testid="tile-clients" icon={Users} tint="text-emerald-400" value={c.clients ?? 0} label={t("Klienci")} />
          <Tile testid="tile-fibers" icon={Split} tint="text-cyan-400" value={s?.free_fibers ?? 0} sub={`/ ${s?.total_fibers ?? 0}`} label={t("Wolne włókna")} />
          <Tile testid="tile-routers" icon={Router} tint="text-sky-400" value={routersOnline} sub={`/ ${routers.length}`} label={t("Routery online")} />
          <Tile testid="tile-switches" icon={Network} tint="text-cyan-400" value={switches.length} label={t("Switche")} />
          <Tile testid="tile-rx-alerts" icon={Zap} tint={rxAlerts.length ? "text-rose-400" : "text-emerald-400"} value={rxAlerts.length} trend={rxTrend} label={t("RX poza normą")} />
          {sidusis && <Tile testid="tile-sidusis" icon={Globe2} tint={sidusis.days_left <= 3 ? "text-rose-400" : "text-cyan-400"} value={sidusis.days_left} sub={`dni do ${sidusis.deadline_day}.`} label="SIDUSIS termin" />}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
          <div className="kv-glass rounded-xl p-4">
            <div className="text-[11px] uppercase tracking-wider text-cyan-400/70 mb-2">{t("Stan ONU")}</div>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={onuData} dataKey="value" nameKey="name" innerRadius={55} outerRadius={85} paddingAngle={3} stroke="none">
                    {onuData.map((e, i) => <Cell key={i} fill={e.color} />)}
                  </Pie>
                  <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #334155", fontSize: 12, borderRadius: 8 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex justify-center gap-4 text-xs">
              <span className="text-emerald-400">● {t("online")}: {onuOnline}</span>
              <span className="text-rose-400">● {t("offline")}: {onuOffline}</span>
            </div>
          </div>

          <div className="kv-glass rounded-xl p-4 lg:col-span-2">
            <div className="text-[11px] uppercase tracking-wider text-cyan-400/70 mb-2">{t("Infrastruktura sieci")}</div>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={infra} margin={{ top: 8, right: 8, bottom: 0, left: -14 }}>
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={{ stroke: "#1e293b" }} tickLine={false} />
                  <Tooltip cursor={{ fill: "rgba(6,182,212,0.08)" }} contentStyle={{ background: "#0f172a", border: "1px solid #334155", fontSize: 12, borderRadius: 8 }} />
                  <Bar dataKey="v" radius={[6, 6, 0, 0]} fill="#22d3ee" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {rxAlerts.length > 0 && (
          <div className="kv-glass rounded-xl p-4" data-testid="rx-alerts-card">
            <div className="text-[11px] uppercase tracking-wider text-cyan-400/70 mb-2 flex items-center gap-1.5"><Zap size={13} className="text-rose-400" /> {t("Klienci z RX poza normą")} ({rxAlerts.length})</div>
            <div className="space-y-1 max-h-64 overflow-auto">
              {rxAlerts.map((a) => (
                <div key={a.id} data-testid="rx-alert-row" onClick={() => {
                  window.dispatchEvent(new CustomEvent("fp-open-client360", { detail: { clientId: a.id } }));
                }} title={t("Otwórz w Widoku 360°")} className="flex items-center gap-2 text-xs bg-slate-900/40 hover:bg-cyan-500/10 hover:border-cyan-500/40 border border-transparent rounded px-2 py-1.5 cursor-pointer transition-colors">
                  <span className={`w-2 h-2 rounded-full shrink-0 ${a.status === "krytyczny" ? "bg-red-500" : "bg-amber-400"}`} />
                  <span className="text-slate-200 truncate flex-1">{a.name}</span>
                  <span className="text-cyan-300 font-mono">{a.measured_rx_dbm != null ? a.measured_rx_dbm : "—"} dBm</span>
                  <span className="text-slate-500 hidden sm:inline">/ oczek. {a.expected_rx_dbm ?? "—"}</span>
                  <span className={`text-[10px] uppercase ${a.status === "krytyczny" ? "text-red-400" : "text-amber-300"}`}>{a.status}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="kv-glass rounded-xl p-4" data-testid="rx-autopoll-card">
          <div className="text-[11px] uppercase tracking-wider text-cyan-400/70 mb-2 flex items-center gap-1.5"><Radio size={13} /> {t("Auto-pomiar RX (SNMP)")}</div>
          <label className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer">
            <input type="checkbox" data-testid="rx-autopoll-enabled" checked={autoRx.enabled} onChange={(e) => saveAutoRx({ enabled: e.target.checked })} className="w-4 h-4 accent-cyan-500" />
            {t("Włącz cykliczny zapis RX do historii")}
          </label>
          <div className="flex items-center gap-2 text-xs text-slate-400 mt-2">
            <span>{t("Co ile minut")}:</span>
            <input type="number" min="15" data-testid="rx-autopoll-interval" value={autoRx.interval}
              onChange={(e) => setAutoRx((a) => ({ ...a, interval: e.target.value }))}
              onBlur={() => saveAutoRx({ interval: Number(autoRx.interval) })}
              className="w-20 bg-slate-900 border border-slate-700 rounded px-2 py-1 outline-none focus:ring-1 focus:ring-cyan-500" />
            <span className="text-slate-600">({t("min. 15")})</span>
          </div>
          <div className="flex items-center gap-2 text-xs text-slate-400 mt-2">
            <span>{t("Próg alertu pogorszenia")}:</span>
            <input type="number" min="1" step="0.5" data-testid="rx-alert-threshold" value={autoRx.threshold}
              onChange={(e) => setAutoRx((a) => ({ ...a, threshold: e.target.value }))}
              onBlur={() => saveAutoRx({ threshold: Number(autoRx.threshold) })}
              className="w-20 bg-slate-900 border border-slate-700 rounded px-2 py-1 outline-none focus:ring-1 focus:ring-cyan-500" />
            <span className="text-slate-600">dB</span>
          </div>
          {autoRx.lastRun && (
            <div className="text-[11px] text-slate-500 mt-2" data-testid="rx-autopoll-status">
              {t("Ostatni cykl")}: {String(autoRx.lastRun).slice(0, 16).replace("T", " ")} · {t("zaktualizowano ONU")}: {autoRx.lastCount ?? 0}
            </div>
          )}
          {geoPending > 0 && (
            <div className="text-[11px] mt-2 flex items-center gap-1.5" data-testid="teryt-unmatched">
              <Radio size={12} className="text-amber-400" />
              <span className="text-slate-400">Adresy niedopasowane do TERYT:</span>
              <b className="text-amber-300">{geoPending}</b>
              <button onClick={regeocode} data-testid="teryt-rematch" className="ml-1 text-[10px] px-1.5 py-0.5 rounded bg-slate-800 text-cyan-300 hover:bg-slate-700">Dopasuj ponownie</button>
              {!hasTeryt && <span className="text-[10px] text-rose-300 ml-1">— najpierw wgraj słownik TERYT (moduł UKE)</span>}
            </div>
          )}
        </div>

        {sidusis && (
          <div className="kv-glass rounded-xl p-4" data-testid="sidusis-card">
            <div className="text-[11px] uppercase tracking-wider text-cyan-400/70 mb-2 flex items-center gap-1.5"><Globe2 size={13} /> SIDUSIS — raportowanie {sidusis.training && <span className="text-amber-400">[szkoleniowe]</span>} {!sidusis.enabled && <span className="text-amber-300">(wyłączone)</span>}</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div><div className={`text-2xl font-bold ${sidusis.days_left <= 3 ? "text-rose-400" : "text-white"}`}>{sidusis.days_left}</div><div className="text-[11px] text-cyan-400/70">dni do {sidusis.deadline_day}. dnia</div></div>
              <div><div className="text-2xl font-bold text-white">{sidusis.valid_ranges}<span className="text-sm text-slate-400 font-normal"> / {sidusis.total_ranges}</span></div><div className="text-[11px] text-cyan-400/70">zasięgi gotowe</div></div>
              <div className="col-span-2">
                <div className="text-[11px] text-cyan-400/70 mb-0.5">Ostatnia wysyłka</div>
                {sidusis.last_send ? (
                  <div className="text-xs text-slate-300" data-testid="sidusis-last-send">{sidusis.last_send.kind} · {String(sidusis.last_send.at).slice(0, 16).replace("T", " ")}<br /><span className="text-emerald-400">+{sidusis.last_send.created}</span> · ~{sidusis.last_send.updated} · <span className="text-rose-400">{sidusis.last_send.failed} bł.</span></div>
                ) : <div className="text-xs text-slate-500" data-testid="sidusis-last-send">brak wysyłek</div>}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
